import { select, Selection } from 'd3-selection'
import { interrupt } from 'd3-transition'
import { Axis as D3Axis, axisBottom, axisLeft, axisRight, axisTop } from 'd3-axis'
import { NumberValue } from 'd3-scale'

// Core
import { XYComponentCore } from '@/core/xy-component'

// Types
import { Rect } from '@/types/misc'
import { Position } from '@/types/position'
import { ContinuousScale } from '@/types/scale'
import { Spacing } from '@/types/spacing'
import { FitMode, TextAlign, TrimMode, UnovisText, UnovisTextOptions, VerticalAlign } from '@/types/text'

// Utils
import { smartTransition } from '@/utils/d3'
import { estimateWrappedTextHeight, getWrappedText, renderTextToSvgTextElement, textAlignToAnchor, trimSVGText, wrapSVGText } from '@/utils/text'
import { getCachedComputedTextLength, getPreciseStringLengthPx } from '@/utils/text-measure'
import { isEqual, isFunction } from '@/utils/data'
import { getRotatedRectAabb } from '@/utils/misc'
import { hideOverlappingLabels } from '@/utils/text-overlap'
import { UNOVIS_TEXT_DEFAULT } from '@/styles/index'

// Local Types
import { AxisType, TickSets, TickValues } from './types'

// Local Utils
import {
  findFittingTickValues,
  getNestedTickValues,
  getTickValueCandidates,
  getTickValueSubsetCandidates,
  mergeTickValues,
  tickKey,
} from './tick-fit'

// Config
import { AxisDefaultConfig, AxisConfigInterface } from './config'

// Styles
import * as s from './style'

type TickTextStyle = {
  fontSize: number;
  fontFamily: string;
  fontWeight?: number;
}

/** Minimum on-screen gap between tick labels (negative `tolerance` of `resolveRectsOverlap`
 * expands the rects). The tick fitting and the overlap safety net must use the same value,
 * otherwise the fitted sets wouldn't survive the overlap pass */
const TICK_LABEL_OVERLAP_TOLERANCE_PX = -5

export class Axis<Datum> extends XYComponentCore<Datum, AxisConfigInterface<Datum>> {
  static selectors = s
  protected _defaultConfig: AxisConfigInterface<Datum> = AxisDefaultConfig
  public config: AxisConfigInterface<Datum> = this._defaultConfig
  private axisGroup: Selection<SVGGElement, unknown, SVGGElement, unknown>
  private gridGroup: Selection<SVGGElement, unknown, SVGGElement, unknown>

  private _axisRawBBox: DOMRect
  private _axisSizeBBox: SVGRect
  private _requiredMargin: Spacing
  private _defaultNumTicks = 3
  private _collideTickLabelsAnimFrameId: ReturnType<typeof requestAnimationFrame>
  private _tickTextStyleCached: TickTextStyle

  protected events = {}

  constructor (config?: AxisConfigInterface<Datum>) {
    super()
    this.setConfig(config)

    this.g.attr('axis-type', this.config.type)

    this.axisGroup = this.g.append('g')
    this.gridGroup = this.g.append('g')
      .attr('class', s.grid)
  }

  public setConfig (config: AxisConfigInterface<Datum>): void {
    // `tickTextFontSize` is the only config option the cached font metrics depend on
    // (CSS-driven font changes are not tracked, as re-resolving the style forces a reflow)
    if (config?.tickTextFontSize !== this.config.tickTextFontSize) this._tickTextStyleCached = undefined
    super.setConfig(config)
  }

  /** Renders axis to an invisible grouped to calculate automatic chart margins */
  public preRender (): void {
    const { config } = this
    const axisRenderHelperGroup = this.g.append('g').attr('opacity', 0)

    // Measure the full fitted set, so the margins account for its label bleed. Deliberately
    // not the `labeled` subset: margins from it would depend on the extreme-label drops, which
    // themselves depend on the margins — an unstable feedback making layout resize-path-dependent
    this._renderAxis(axisRenderHelperGroup, 0, this._getFittingTickValues()?.fittedTicks)

    // Align tick text
    if (config.tickTextAlign) this._alignTickLabels(axisRenderHelperGroup)

    // Store axis raw BBox (without the label) for further label positioning (see _renderAxisLabel)
    this._axisRawBBox = axisRenderHelperGroup.node().getBBox()

    // Render label and store total axis size and required margins
    this._renderAxisLabel(axisRenderHelperGroup)
    this._axisSizeBBox = this._getAxisSize(axisRenderHelperGroup)
    this._requiredMargin = this._getRequiredMargin(this._axisSizeBBox)

    axisRenderHelperGroup.remove()
  }

  public getPosition (): Position {
    const { config: { type, position } } = this
    return (position ?? ((type === AxisType.X) ? Position.Bottom : Position.Left)) as Position
  }

  private _getAxisSize (selection: Selection<SVGGElement, unknown, SVGGElement, undefined>): SVGRect {
    const bBox = selection.node().getBBox()
    return bBox
  }

  private _getRequiredMargin (axisSize = this._axisSizeBBox): Spacing {
    const { config: { type, position } } = this

    switch (type) {
      case AxisType.X: {
        const tolerancePx = 1
        const xEnd = this._axisSizeBBox.x + this._axisSizeBBox.width

        const left = this._axisSizeBBox.x < 0 ? Math.abs(this._axisSizeBBox.x) : 0
        const right = (xEnd - this._width) > tolerancePx ? xEnd - this._width : 0

        switch (position) {
          case Position.Top: return { top: axisSize.height, left, right }
          case Position.Bottom: default: return { bottom: axisSize.height, left, right }
        }
      }
      case AxisType.Y: {
        const bleedY = axisSize.height > this._height ? (axisSize.height - this._height) / 2 : 0
        const top = bleedY
        const bottom = bleedY

        switch (position) {
          case Position.Right: return { right: axisSize.width, top, bottom }
          case Position.Left: default: return { left: axisSize.width, top, bottom }
        }
      }
    }
  }

  getRequiredMargin (): Spacing {
    return this._requiredMargin
  }

  /** Calculates axis transform:translate offset based on passed container margins */
  getOffset (containerMargin: Spacing): {left: number; top: number} {
    const { config: { type, position } } = this

    switch (type) {
      case AxisType.X:
        switch (position) {
          case Position.Top: return { top: containerMargin.top, left: containerMargin.left }
          case Position.Bottom: default: return { top: containerMargin.top + this._height, left: containerMargin.left }
        }
      case AxisType.Y:
        switch (position) {
          case Position.Right: return { top: containerMargin.top, left: containerMargin.left + this._width }
          case Position.Left: default: return { top: containerMargin.top, left: containerMargin.left }
        }
    }
  }

  public _render (duration = this.config.duration, selection = this.axisGroup): void {
    const { config } = this

    // Adaptive tick sets: the whole original set renders as tick marks (merged with the rare
    // fitted values missing from it, e.g. on time scales), and only the fitted subset is labeled.
    // When `tickSets` is undefined (the option is off or not applicable), `_renderAxis` computes
    // the tick values itself and labels all of them
    const tickSets = this._getFittingTickValues()
    const renderTickValues = tickSets && mergeTickValues(tickSets.originalTicks, tickSets.fittedTicks)
    const labeledTickKeys = tickSets && new Set(tickSets.labeledTicks.map(tickKey))

    this._renderAxis(selection, duration, renderTickValues, labeledTickKeys)
    this._renderAxisLabel(selection)

    if (config.gridLine) {
      // The grid renders a line per tick mark, but only the lines of the labeled ticks are
      // visible — hiding the rest with a class keeps the elements stable across renders,
      // so grid changes animate in CSS the same way the labels do
      const gridGen = this._buildGrid(renderTickValues)
      // Interrupting all active transitions first to prevent them from being stuck.
      // Somehow we see it happening in Angular apps.
      this.gridGroup.selectAll('*').interrupt()
      smartTransition(this.gridGroup, duration).call(gridGen).style('opacity', 1)
      this.gridGroup.selectAll<SVGGElement, number | Date>('g.tick')
        .classed(s.gridTickHidden, d => labeledTickKeys ? !labeledTickKeys.has(tickKey(d)) : false)
    } else {
      smartTransition(this.gridGroup, duration).style('opacity', 0)
    }

    if (config.tickTextAlign) this._alignTickLabels()
    this._resolveTickLabelOverlap(selection)
  }

  private _getAxisGen (): D3Axis<NumberValue | Date> {
    const { config } = this

    switch (config.type) {
      case AxisType.X:
        switch (config.position) {
          case Position.Top: return axisTop(this.xScale)
          case Position.Bottom: default: return axisBottom(this.xScale)
        }
      case AxisType.Y:
        switch (config.position) {
          case Position.Right: return axisRight(this.yScale)
          case Position.Left: default: return axisLeft(this.yScale)
        }
    }
  }

  private _buildAxis (): D3Axis<NumberValue | Date> {
    const { config: { tickPadding, tickSize } } = this

    const tickSizeInner = Array.isArray(tickSize) ? tickSize[0] : tickSize
    const tickSizeOuter = Array.isArray(tickSize) ? tickSize[1] : tickSize
    const ticks = this._getNumTicks()
    const axisGen = this._getAxisGen()
    axisGen.ticks(ticks).tickPadding(tickPadding).tickSizeInner(tickSizeInner).tickSizeOuter(tickSizeOuter)

    return axisGen
  }

  private _buildGrid (tickValuesOverride?: TickValues): D3Axis<NumberValue | Date> {
    const { config } = this

    const gridGen = this._getAxisGen()
    gridGen
      .tickSize(config.type === AxisType.X ? -this._height : -this._width)
      .tickSizeOuter(0)
      .tickFormat(() => '')

    const numTicks = this._getNumTicks() * 2
    const gridScale = gridGen.scale<ContinuousScale>()
    const scaleDomain = gridScale.domain()

    const getGridMinMaxTicksOnlyValues = (): number[] | Date[] => {
      if (!config.minMaxTicksOnlyShowGridLines) return scaleDomain

      const tickValues = gridScale.ticks(numTicks)
      if (tickValues.length < 2) return scaleDomain

      // If the last tick is far enough from the domain max value, we add it to the tick values to draw the grid line
      const tickValuesStep = +tickValues[1] - +tickValues[0]
      const domainMaxValue = scaleDomain[1]
      const diff = +domainMaxValue - +tickValues[tickValues.length - 1]

      return diff > tickValuesStep / 2 ? [...tickValues, domainMaxValue] as (number[] | Date[]) : tickValues
    }

    const tickValues = tickValuesOverride ?? (
      config.tickValues
        ? this._getConfiguredTickValues()
        : this._shouldRenderMinMaxTicksOnly()
          ? getGridMinMaxTicksOnlyValues()
          : gridScale.ticks(numTicks)
    )

    gridGen.tickValues(tickValues)

    return gridGen
  }

  private _renderAxis (selection = this.axisGroup, duration = this.config.duration, tickValuesOverride?: TickValues, labeledTickKeys?: Set<string>): void {
    const { config } = this

    const axisGen = this._buildAxis()
    const axisScale = axisGen.scale<ContinuousScale>()
    const tickValues: TickValues = tickValuesOverride ?? (
      config.tickValues
        ? this._getConfiguredTickValues()
        : this._shouldRenderMinMaxTicksOnly()
          ? axisScale.domain()
          : axisScale.ticks(this._getNumTicks())
    )
    const tickCount = tickValues.length
    axisGen.tickValues(tickValues)

    // Interrupting all active transitions first to prevent them from being stuck.
    // Somehow we see it happening in Angular apps.
    selection.selectAll('*').interrupt()
    const transition = smartTransition(selection, duration).call(axisGen)

    // Unset D3's default y and dy attributes because we're going to set them manually in the renderTextToSvgTextElement function
    selection.selectAll<SVGTextElement, number | Date>('text')
      .attr('dy', null)
      .attr('y', null)

    // Resolving tick label overlap after the animation is over
    transition.on('end', () => {
      this._resolveTickLabelOverlap(selection)
    })

    const ticks = selection.selectAll<SVGGElement, number | Date>('g.tick')

    ticks
      .classed(s.tick, true)
      .style('font-size', config.tickTextFontSize)

    // Selecting the <text> elements of the ticks to apply formatting. By default, this selection
    // will include exiting elements, so we're filtering them out.
    const tickText = selection.selectAll<SVGTextElement, number | Date>('g.tick > text')
      .filter(tickValue => tickValues.some((t: number | Date) => isEqual(tickValue, t))) // We use isEqual to compare Dates
      .classed(s.tickLabel, true)
      .classed(s.tickLabelHideable, Boolean(config.tickTextHideOverlapping))
      // Ticks outside the fitted set render as unlabeled tick marks
      .classed(s.tickTextHidden, tickValue => labeledTickKeys ? !labeledTickKeys.has(tickKey(tickValue)) : false)
      .style('fill', config.tickTextColor) as Selection<SVGTextElement, number | Date, SVGGElement, unknown>

    // Stale inline opacity (set by the overlap pass) would override the hiding class
    if (labeledTickKeys) {
      tickText.filter(tickValue => !labeledTickKeys.has(tickKey(tickValue))).style('opacity', null)
    }

    // Marking exiting elements
    selection.selectAll<SVGTextElement, number | Date>('g.tick > text')
      .filter(tickValue => !tickValues.some((t: number | Date) => isEqual(tickValue, t)))
      .classed(s.tickTextExiting, true)

    // We interrupt the transition on tick's <text> to make it 'wrappable'
    tickText.nodes().forEach(node => interrupt(node))

    const tickSize = axisGen.tickSize()
    const axisPosition = this.getPosition()
    // Fair-share label width counts labeled ticks only, matching what the tick fitting measured
    const textMaxWidth = this._getTickTextMaxWidth(labeledTickKeys?.size ?? tickCount)
    tickText.each((value: number | Date, i: number, elements: ArrayLike<SVGTextElement>) => {
      let text = config.tickFormat?.(value, i, tickValues as number[] | Date[]) ?? `${value}`
      const textElement = elements[i] as SVGTextElement
      const tickTextStyle = this._getTickTextStyle(textElement)

      // Calculate the text offset based on the axis position and the tick size
      const [textOffsetX, textOffsetY] = this._getTickTextOffset(axisPosition, tickSize, tickTextStyle.fontSize)

      const textOptions: UnovisTextOptions = {
        ...this._getTickTextOptions(textMaxWidth),
        x: textOffsetX,
        y: textOffsetY,
      }

      if (config.tickTextFitMode === FitMode.Trim) {
        const textElementSelection = select<SVGTextElement, string>(textElement).text(text)
        trimSVGText(textElementSelection, textMaxWidth, config.tickTextTrimType as TrimMode)
        text = select<SVGTextElement, string>(textElement).text()
      }

      const textBlock: UnovisText = { text, ...tickTextStyle }
      const dominantBaseline = config.type === AxisType.X ? 'central' : 'hanging'
      renderTextToSvgTextElement(textElement, textBlock, textOptions, dominantBaseline)
    })

    selection
      .classed(s.axis, true)
      .classed(s.hideTickLine, !config.tickLine)
      .classed(s.hideDomain, !config.domainLine)

    if (config.fullSize) {
      const path = this._getFullDomainPath(0)
      smartTransition(selection.select('.domain'), duration).attr('d', path)
    }
  }

  private _resolveTickLabelOverlap (selection = this.axisGroup): void {
    const { config } = this
    const tickTextSelection = selection.selectAll<SVGTextElement, number | Date>(`g.tick > text:not(.${s.tickTextExiting}):not(.${s.tickTextHidden})`)

    if (!config.tickTextHideOverlapping) {
      tickTextSelection.style('opacity', null)
      return
    }

    cancelAnimationFrame(this._collideTickLabelsAnimFrameId)
    // Colliding labels in the next frame to prevent forced reflow
    this._collideTickLabelsAnimFrameId = requestAnimationFrame(() => {
      hideOverlappingLabels(tickTextSelection, { tolerance: TICK_LABEL_OVERLAP_TOLERANCE_PX })
    })
  }

  /** Finds the largest tick set whose labels don't overlap (see {@link findFittingTickValues}),
   * along with the original tick set — its remaining ticks get rendered as unlabeled tick marks.
   * The candidates are "nice" d3 tick sets, or every-k-th subsets when explicit `tickValues` are set.
   * Returns `undefined` when the search is not applicable, falling back to the default tick generation. */
  private _getFittingTickValues (): TickSets | undefined {
    const { config } = this
    if (!config.tickTextAdaptiveSets) return undefined
    if (this._shouldRenderMinMaxTicksOnly()) return undefined

    const scale = (config.type === AxisType.X ? this.xScale : this.yScale) as ContinuousScale
    const maxNumTicks = Math.ceil(this._getNumTicks())
    const configuredTickValues = this._getConfiguredTickValues()

    const candidates = configuredTickValues
      ? getTickValueSubsetCandidates(configuredTickValues)
      : getTickValueCandidates(scale, maxNumTicks)
    const fitting = findFittingTickValues(candidates, values => this._getTickLabelRects(values), TICK_LABEL_OVERLAP_TOLERANCE_PX)
    if (!fitting) return undefined

    const originalTicks = configuredTickValues ?? getNestedTickValues(scale, maxNumTicks, candidates[0])
    return { ...fitting, originalTicks }
  }

  /** Fair-share width available to a tick label before it gets wrapped or trimmed */
  private _getTickTextMaxWidth (labelCount: number): number {
    const { config } = this
    return config.tickTextWidth ||
      (config.type === AxisType.X ? this._containerWidth / (labelCount + 1) : this._containerWidth / 5)
  }

  /** Label rendering options shared between `_renderAxis` and the tick fitting predictions
   * (see `_getTickLabelRects`) — the two must stay identical for the predicted geometry
   * to match the render. Precise (cached) measurements are used for wrapping for the same reason */
  private _getTickTextOptions (textMaxWidth: number): UnovisTextOptions {
    const { config } = this
    return {
      verticalAlign: config.type === AxisType.X ? VerticalAlign.Top : VerticalAlign.Middle,
      width: textMaxWidth,
      textRotationAngle: config.tickTextAngle,
      separator: config.tickTextSeparator,
      wordBreak: config.tickTextForceWordBreak,
      fastMode: false,
    }
  }

  /** Predicts tick label rects using the same text wrapping and cached measurements
   * the renderer itself relies on. */
  private _getTickLabelRects (values: TickValues): Rect[] {
    const { config } = this
    const isX = config.type === AxisType.X
    const scale = (isX ? this.xScale : this.yScale) as ContinuousScale
    const style = this._getTickTextStyle()
    const textOptions = this._getTickTextOptions(this._getTickTextMaxWidth(values.length))
    const lineHeightPx = style.fontSize * UNOVIS_TEXT_DEFAULT.lineHeight
    const angleRad = (config.tickTextAngle ?? 0) / 180 * Math.PI

    return values.map((value, i) => {
      const text = config.tickFormat?.(value, i, values as number[] | Date[]) ?? `${value}`

      // Label size, computed the same way _renderAxis will compute it
      let width: number
      let height: number
      if (config.tickTextFitMode === FitMode.Trim) {
        // Approximation of trimSVGText: a trimmed label can't be wider than its width budget
        const fullWidth = getPreciseStringLengthPx(text, style.fontFamily, style.fontSize, style.fontWeight)
        width = Math.min(fullWidth, textOptions.width)
        height = lineHeightPx
      } else {
        const wrapped = getWrappedText({ text, ...style }, textOptions.width, undefined, textOptions.fastMode, textOptions.separator, textOptions.wordBreak)
        const lines = wrapped.flatMap(block => block._lines)
        const lineWidths = lines.map(line => getPreciseStringLengthPx(line, style.fontFamily, style.fontSize, style.fontWeight))
        width = Math.max(0, ...lineWidths)
        height = estimateWrappedTextHeight(wrapped)
      }

      // Label extent relative to its anchor at the tick position
      const position = scale(value as never)
      const tickPosition: [number, number] = isX ? [position, 0] : [0, position]
      const textAlign = isFunction(config.tickTextAlign)
        ? config.tickTextAlign(value, i, values as number[] | Date[], tickPosition, this._width, this._height)
        : config.tickTextAlign

      let x0: number
      if (!isX) {
        // Y axis labels extend from the axis towards the chart edge
        x0 = this.getPosition() === Position.Left ? -width : 0
      } else if (textAlign === TextAlign.Left) {
        x0 = 0
      } else if (textAlign === TextAlign.Right) {
        x0 = -width
      } else {
        // X axis labels are center-anchored by default
        x0 = -width / 2
      }
      const y0 = isX ? -lineHeightPx / 2 : -height / 2

      const localRect = { x: x0, y: y0, width, height }
      const rect = angleRad ? getRotatedRectAabb(localRect, angleRad) : localRect
      return { ...rect, x: tickPosition[0] + rect.x, y: tickPosition[1] + rect.y }
    })
  }

  /** Returns the cached tick label text style, resolving it on first use — from the given
   * element when provided, otherwise from a throwaway element with the same classes */
  private _getTickTextStyle (textElement?: SVGTextElement): TickTextStyle {
    if (this._tickTextStyleCached) {
      return this._tickTextStyleCached
    }

    const helper = textElement ? undefined : this.g.append('g')
      .attr('class', s.tick)
      .style('font-size', this.config.tickTextFontSize)
    const element = textElement ?? helper.append('text').attr('class', s.tickLabel).node()
    const styleDeclaration = getComputedStyle(element)
    this._tickTextStyleCached = {
      fontSize: Number.parseFloat(styleDeclaration.fontSize),
      fontFamily: styleDeclaration.fontFamily,
      fontWeight: Number.parseFloat(styleDeclaration.fontWeight) || undefined,
    }
    helper?.remove()
    return this._tickTextStyleCached
  }

  private _getNumTicks (): number {
    const { config: { type, numTicks, tickSpacing } } = this

    if (numTicks) return numTicks

    if (type === AxisType.X) {
      const xRange = this.xScale.range() as [number, number]
      const width = xRange[1] - xRange[0]
      return Math.max(1, Math.floor(width / tickSpacing))
    }

    if (type === AxisType.Y) {
      const yRange = this.yScale.range() as [number, number]
      const height = Math.abs(yRange[0] - yRange[1])
      return Math.max(1, Math.pow(height, 0.85) / 25)
    }

    return this._defaultNumTicks
  }

  private _getConfiguredTickValues (): number[] | null {
    const { config } = this
    const scale = config.type === AxisType.X ? this.xScale : this.yScale
    const scaleDomain = scale?.domain() as [number, number]

    if (config.tickValues) {
      return config.tickValues.filter(v => (v >= scaleDomain[0]) && (v <= scaleDomain[1]))
    }

    return null
  }

  private _shouldRenderMinMaxTicksOnly (): boolean {
    const { config } = this
    if (config.minMaxTicksOnly) return true
    // The tick fitting owns the narrow-width behavior when enabled
    if (config.tickTextAdaptiveSets) return false
    return config.type === AxisType.X && this._width < config.minMaxTicksOnlyWhenWidthIsLess
  }

  private _getFullDomainPath (tickSize = 0): string {
    const { config: { type } } = this
    switch (type) {
      case AxisType.X: return `M0.5, ${tickSize} V0.5 H${this._width + 0.5} V${tickSize}`
      case AxisType.Y: return `M${-tickSize}, ${this._height + 0.5} H0.5 V0.5 H${-tickSize}`
    }
  }

  private _renderAxisLabel (selection = this.axisGroup): void {
    const { type, label, labelMargin, labelFontSize, labelTextFitMode, labelTextSeparator } = this.config

    // Remove the old label first to calculate the axis size properly
    selection.selectAll(`.${s.label}`).remove()

    if (!label) return

    // Calculate label position and rotation
    const axisPosition = this.getPosition()
    // We always use this.axisRenderHelperGroup to calculate the size of the axis because
    //    this.axisGroup will give us incorrect values due to animation
    const { width: axisWidth, height: axisHeight } = this._axisRawBBox ?? selection.node().getBBox()

    const rotation = type === AxisType.Y ? -90 : 0

    // Create the text element (without transform first)
    const textElement = selection
      .append('text')
      .attr('class', s.label)
      .attr('dy', `${this._getLabelDY()}em`)
      .style('font-size', labelFontSize)
      .style('fill', this.config.labelColor)

    // Set the text content
    textElement.text(label)

    let isWrapped = false
    if (labelTextFitMode === FitMode.Wrap) {
      // For Y-axis, use the chart height as the maximum width before rotation
      const maxWidth = type === AxisType.Y ? this._height : this._width
      const currentTextWidth = getCachedComputedTextLength(textElement.node())

      if (currentTextWidth > maxWidth) {
        wrapSVGText(textElement, maxWidth, labelTextSeparator)
        isWrapped = true
      }
    }

    // Calculate offset after wrapping to get accurate dimensions
    let labelWidth = axisWidth
    let labelHeight = axisHeight
    if (labelTextFitMode === FitMode.Wrap) {
      const labelBBox = textElement.node().getBBox()
      labelWidth = labelBBox.width
      labelHeight = labelBBox.height
    } else {
      const trimWidth = type === AxisType.X ? labelWidth : labelHeight
      trimSVGText(textElement, trimWidth, this.config.labelTextTrimType as TrimMode)

      const trimmedBBox = textElement.node().getBBox()
      labelWidth = trimmedBBox.width
      labelHeight = trimmedBBox.height
    }

    /*
      we need to calculate the offset for the label based on the position and the fit mode
      for offsetX, applying Y label we need to check if it's wrap or trim, then set the offset accordingly.
      Same for offsetY, need to consider x label.
    */
    const offsetX = type === AxisType.X
      ? this._width / 2
      : type === AxisType.Y && labelTextFitMode === FitMode.Wrap && isWrapped
        ? (axisPosition === Position.Left)
          ? -axisWidth - labelHeight / 2 - 10 // there's a 10px gap between tick label and Position.Right Y label, so offset as well in the Position.Left case
          : axisWidth
        : (-1) ** (+(axisPosition === Position.Left)) * axisWidth
    const offsetY = type === AxisType.Y
      ? this._height / 2
      : type === AxisType.X && labelTextFitMode === FitMode.Wrap
        ? (axisPosition === Position.Top) ? -axisHeight - labelHeight / 2 : axisHeight
        : (-1) ** (+(axisPosition === Position.Top)) * axisHeight

    const marginX = type === AxisType.X ? 0 : (-1) ** (+(axisPosition === Position.Left)) * labelMargin
    const marginY = type === AxisType.X ? (-1) ** (+(axisPosition === Position.Top)) * labelMargin : 0

    // Apply transform and rotation after all calculations
    textElement.attr('transform', `translate(${offsetX + marginX},${offsetY + marginY}) rotate(${rotation})`)
  }

  private _getLabelDY (): number {
    const { type, position } = this.config
    switch (type) {
      case AxisType.X:
        switch (position) {
          case Position.Top: return 0
          case Position.Bottom: default: return 0.75
        }
      case AxisType.Y:
        switch (position) {
          case Position.Right: return 0.75
          case Position.Left: default: return -0.25
        }
    }
  }

  private _getTickTextOffset (axisPosition: Position, tickSize: number, fontSize: number): [number, number] {
    const { config } = this
    const angleRad = (config.tickTextAngle ?? 0) / 180 * Math.PI
    const baseOffset = tickSize + config.tickPadding

    if (config.type === AxisType.X) {
      const direction = axisPosition === Position.Bottom ? 1 : -1
      return [
        direction * baseOffset * Math.sin(angleRad),
        direction * (baseOffset + fontSize / 2) * Math.cos(angleRad),
      ]
    } else {
      const direction = axisPosition === Position.Right ? 1 : -1
      return [
        direction * baseOffset * Math.cos(angleRad),
        direction * baseOffset * Math.sin(angleRad),
      ]
    }
  }

  private _alignTickLabels (axisGroup = this.axisGroup): void {
    const { config: { type, tickTextAlign, tickTextAngle, position } } = this
    const activeTickTexts = axisGroup.selectAll<SVGTextElement, number | Date>(`g.tick > text:not(.${s.tickTextExiting})`)
    const ticksData = activeTickTexts.data() as number[] | Date[]

    activeTickTexts.each((_, i, elements) => {
      const tickTextElement = elements[i] as SVGTextElement
      const tickDatum = ticksData[i]
      // Compute the tick's target position from the scale rather than reading the DOM transform,
      // which would return an interpolated value during a transition.
      const tickPosition: [number, number] = type === AxisType.X
        ? [this.xScale(tickDatum as never), 0]
        : [0, this.yScale(tickDatum as never)]
      const textAlign = (isFunction(tickTextAlign) ? tickTextAlign(tickDatum, i, ticksData, tickPosition, this._width, this._height) : tickTextAlign) as TextAlign
      const textAnchor = textAlignToAnchor(textAlign)
      const translateX = type === AxisType.X ? 0 : this._getYTickTextTranslate(textAlign, position as Position)

      const translateValue = tickTextAngle ? `translate(${translateX},0) rotate(${tickTextAngle})` : `translate(${translateX},0)`
      select(tickTextElement)
        .attr('transform', translateValue)
        .attr('text-anchor', textAnchor)
    })
  }

  private _getYTickTextTranslate (textAlign: TextAlign, axisPosition: Position = Position.Left): number {
    /*
      Default in D3 is 9, tickPadding is the spacing in pixels between the tick and it's label. Default: `8`
    */
    const defaultTickTextSpacingPx = this.config.tickPadding + 1

    // this._axisRawBBox will be undefined when autoMargin is undefined
    const width = (this._axisRawBBox?.width ?? this.axisGroup.node()?.getBBox().width ?? 0) - defaultTickTextSpacingPx

    switch (textAlign) {
      case TextAlign.Left: return axisPosition === Position.Left ? width * -1 : 0
      case TextAlign.Right: return axisPosition === Position.Left ? 0 : width
      case TextAlign.Center: return axisPosition === Position.Left ? width * (-0.5) : width * 0.5
      default: return 0
    }
  }

  protected _onDestroy (): void {
    cancelAnimationFrame(this._collideTickLabelsAnimFrameId)
  }
}
